import { useEffect, useState } from "react";
import { CalendarCheck, DownloadSimple, Money, Printer, Receipt, Table, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import DuesMatrix from "./DuesMatrix";

export default function FinanceDuesTab() {
    const [view, setView] = useState("input");
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [items, setItems] = useState([]);
    const [defaults, setDefaults] = useState({});
    const [reminderDays, setReminderDays] = useState({});
    const [amounts, setAmounts] = useState({});
    const [discounts, setDiscounts] = useState({});
    const [methods, setMethods] = useState({});
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [defaultPrompt, setDefaultPrompt] = useState(null);
    const [defaultSaving, setDefaultSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [bulkTarget, setBulkTarget] = useState(null);
    const [bulkAvailable, setBulkAvailable] = useState([]);
    const [bulkSelected, setBulkSelected] = useState([]);
    const [bulkAmount, setBulkAmount] = useState("");
    const [bulkDiscount, setBulkDiscount] = useState("");
    const [bulkMethod, setBulkMethod] = useState("Cash");
    const [bulkSaving, setBulkSaving] = useState(false);

    const openBulk = async (athlete) => {
        setBulkTarget(athlete);
        setBulkSelected([]);
        setBulkAmount(String(defaults[athlete.id] || ""));
        setBulkDiscount("");
        setBulkMethod("Cash");
        try {
            const { data } = await api.get(`/finance/athletes/${athlete.id}/unpaid-months`);
            setBulkAvailable(data.months || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            setBulkTarget(null);
        }
    };

    const toggleBulkMonth = (m) => {
        setBulkSelected((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
    };

    const submitBulk = async () => {
        if (bulkSelected.length === 0) {
            toast.error("Pilih minimal satu bulan");
            return;
        }
        setBulkSaving(true);
        try {
            const { data } = await api.post("/finance/payments/bulk", {
                athlete_id: bulkTarget.id,
                months: bulkSelected,
                amount: Number(bulkAmount || 0),
                discount: Number(bulkDiscount || 0),
                method: bulkMethod,
                note: "",
            });
            toast.success(`${data.created} kwitansi terbit untuk ${bulkTarget.name} — total Rp ${data.total.toLocaleString("id-ID")}`);
            const currentDefault = Number(defaults[bulkTarget.id] || 0);
            const paidAmount = Number(bulkAmount || 0);
            if (paidAmount > 0 && paidAmount !== currentDefault) {
                setDefaultPrompt({ athlete: bulkTarget, amount: paidAmount });
            }
            setBulkTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBulkSaving(false);
        }
    };

    const query = search.trim().toLowerCase();
    const visibleItems = query ? items.filter((item) => item.name.toLowerCase().includes(query)) : items;

    const load = async (selectedMonth = month) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/finance/athletes?month=${selectedMonth}`);
            setItems(data);
            setDefaults(Object.fromEntries(data.map((item) => [item.id, item.default_amount || ""])));
            setReminderDays(Object.fromEntries(data.map((item) => [item.id, item.reminder_day || ""])));
            setAmounts(Object.fromEntries(data.map((item) => [item.id, item.payment?.amount || item.default_amount || ""])));
            setDiscounts({});
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(new Date().toISOString().slice(0, 7));
    }, []);

    const changeMonth = (value) => {
        setMonth(value);
        load(value);
    };

    const saveDefault = async (athlete) => {
        const day = Number(reminderDays[athlete.id] || 0);
        if (day < 0 || day > 31) {
            toast.error("Tanggal pengingat harus antara 1-31");
            return;
        }
        try {
            await api.put(`/finance/athletes/${athlete.id}/default`, { amount: Number(defaults[athlete.id] || 0), reminder_day: day });
            toast.success(`Nominal default & tanggal pengingat ${athlete.name} tersimpan`);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const pay = async (athlete) => {
        try {
            const { data } = await api.post("/finance/payments", {
                athlete_id: athlete.id,
                period_month: month,
                amount: Number(amounts[athlete.id] || 0),
                discount: Number(discounts[athlete.id] || 0),
                payment_date: new Date().toISOString().slice(0, 10),
                method: methods[athlete.id] || "Cash",
                note: "",
            });
            toast.success(`Iuran ${athlete.name} tercatat: ${data.receipt_no}`);
            const paidAmount = Number(amounts[athlete.id] || 0);
            const currentDefault = Number(defaults[athlete.id] || 0);
            if (paidAmount > 0 && paidAmount !== currentDefault) {
                setDefaultPrompt({ athlete, amount: paidAmount });
            }
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const removePayment = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/finance/payments/${deleteTarget.payment.id}`);
            toast.success(`Kwitansi ${deleteTarget.payment.receipt_no} dihapus — catatan keuangan ikut terkoreksi, silakan input ulang jika perlu`);
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setDeleting(false);
        }
    };

    const confirmDefaultFromPrompt = async () => {
        if (!defaultPrompt) return;
        setDefaultSaving(true);
        try {
            await api.put(`/finance/athletes/${defaultPrompt.athlete.id}/default`, { amount: defaultPrompt.amount, reminder_day: Number(reminderDays[defaultPrompt.athlete.id] || 0) });
            toast.success(`Nominal default ${defaultPrompt.athlete.name} diubah menjadi Rp ${defaultPrompt.amount.toLocaleString("id-ID")}`);
            setDefaultPrompt(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setDefaultSaving(false);
        }
    };

    const printReceipt = (payment) => {
        const url = fileUrl(payment.receipt_url);
        const popup = window.open("", "_blank", "width=950,height=650");
        if (!popup) return;
        popup.document.write(`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;background:#eee"><img src="${url}" style="max-width:100%" onload="setTimeout(()=>window.print(),300)" /></body></html>`);
        popup.document.close();
    };

    return (
        <div className="space-y-6" data-testid="finance-dues-tab">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h2 className="font-display uppercase text-2xl">Iuran Bulanan</h2>
                    <p className="text-sm text-racing-muted mt-2">Iuran mulai dicatat September 2026. Nominal default bisa diatur per atlet dan tetap bisa diubah saat input. Isi tanggal masuk agar orang tua otomatis diingatkan via email setiap tanggal itu tiap bulan (jika belum bayar).</p>
                </div>
                <div className="flex items-end gap-4">
                    <div className="flex border border-racing-line" data-testid="dues-view-toggle">
                        <button onClick={() => setView("input")} data-testid="dues-view-input" className={`px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${view === "input" ? "bg-racing-red text-white" : "text-racing-muted hover:text-neutral-900"}`}>Input Bulanan</button>
                        <button onClick={() => setView("matrix")} data-testid="dues-view-matrix" className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${view === "matrix" ? "bg-racing-red text-white" : "text-racing-muted hover:text-neutral-900"}`}><Table size={15} /> Rekap Ceklist</button>
                    </div>
                    {view === "input" && (
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Periode</label>
                            <input type="month" min="2026-09" value={month} onChange={(e) => changeMonth(e.target.value)} data-testid="dues-month-input" className="bg-white border border-racing-line px-4 py-3 text-sm" />
                        </div>
                    )}
                </div>
            </div>

            {view === "matrix" && <DuesMatrix />}

            {view === "input" && (
            <>
            <div className="relative mb-4">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ketik sebagian nama atlet untuk mencari..."
                    data-testid="dues-search-input"
                    className="w-full md:w-96 bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                />
                {query && <p className="mt-1.5 text-xs text-racing-muted" data-testid="dues-search-count">{visibleItems.length} atlet cocok</p>}
            </div>
            <div className="border border-racing-line overflow-x-auto bg-white">
                <table className="w-full text-sm min-w-[980px]">
                    <thead>
                        <tr className="border-b border-racing-line bg-racing-surface">
                            {["Atlet", "Kelas", "Default/Bulan", "Tgl Masuk", "Nominal Bayar", "Diskon", "Metode", "Status/Kwitansi", "Aksi"].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="9" className="px-4 py-8 text-center text-racing-muted">Memuat data atlet...</td></tr>}
                        {!loading && visibleItems.length === 0 && <tr><td colSpan="9" className="px-4 py-8 text-center text-racing-muted" data-testid="dues-empty">{query ? `Tidak ada atlet yang cocok dengan "${search}".` : "Belum ada atlet hasil Daftar Ulang aktif."}</td></tr>}
                        {!loading && visibleItems.map((athlete, index) => (
                            <tr key={athlete.id} className="border-b border-racing-line last:border-0" data-testid={`dues-athlete-row-${index}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        {athlete.photo_url ? <img src={fileUrl(athlete.photo_url)} alt="" className="w-10 h-10 object-cover border border-racing-line" /> : <div className="w-10 h-10 bg-racing-surface border border-racing-line" />}
                                        <div><p className="font-medium text-neutral-900">{athlete.name}</p><p className="text-xs text-racing-muted">{athlete.parent_username}</p></div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">{athlete.class_name}</td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                        <input type="number" min="0" value={defaults[athlete.id] ?? ""} onChange={(e) => setDefaults({ ...defaults, [athlete.id]: e.target.value })} data-testid={`dues-default-${index}`} className="w-32 bg-racing-surface border border-racing-line px-3 py-2 text-xs" />
                                        <button onClick={() => saveDefault(athlete)} data-testid={`dues-save-default-${index}`} className="border border-racing-line px-3 py-2 text-xs text-racing-muted hover:text-racing-red">Simpan</button>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <input type="number" min="1" max="31" value={reminderDays[athlete.id] ?? ""} onChange={(e) => setReminderDays({ ...reminderDays, [athlete.id]: e.target.value })} placeholder="-" data-testid={`dues-reminder-day-${index}`} className="w-16 bg-racing-surface border border-racing-line px-3 py-2 text-xs" />
                                    <p className="mt-1 text-[9px] text-racing-muted">1-31, pengingat WA</p>
                                </td>
                                <td className="px-4 py-3"><input type="number" min="1" value={amounts[athlete.id] ?? ""} onChange={(e) => setAmounts({ ...amounts, [athlete.id]: e.target.value })} data-testid={`dues-amount-${index}`} className="w-32 bg-racing-surface border border-racing-line px-3 py-2 text-xs" /></td>
                                <td className="px-4 py-3">
                                    <input type="number" min="0" value={discounts[athlete.id] ?? ""} onChange={(e) => setDiscounts({ ...discounts, [athlete.id]: e.target.value })} placeholder="0" data-testid={`dues-discount-${index}`} className="w-28 bg-racing-surface border border-racing-line px-3 py-2 text-xs" />
                                    {Number(discounts[athlete.id] || 0) > 0 && (
                                        <p className="mt-1 text-[10px] text-racing-golddeep" data-testid={`dues-net-${index}`}>
                                            Diterima: Rp {Math.max(Number(amounts[athlete.id] || 0) - Number(discounts[athlete.id] || 0), 0).toLocaleString("id-ID")}
                                        </p>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <select value={methods[athlete.id] || "Cash"} onChange={(e) => setMethods({ ...methods, [athlete.id]: e.target.value })} data-testid={`dues-method-${index}`} className="bg-racing-surface border border-racing-line px-3 py-2 text-xs">
                                        <option>Cash</option><option>Transfer</option><option>QRIS</option>
                                    </select>
                                </td>
                                <td className="px-4 py-3">
                                    {athlete.payment ? (
                                        <div className="space-y-2">
                                            <p className="text-xs text-green-700" data-testid={`dues-paid-${index}`}>Lunas • {athlete.payment.receipt_no}</p>
                                            {athlete.payment.whatsapp_status && <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">WA: {athlete.payment.whatsapp_status}</p>}
                                            <div className="flex gap-2">
                                                <a href={fileUrl(athlete.payment.receipt_url)} target="_blank" rel="noopener noreferrer" data-testid={`receipt-download-${index}`} className="p-2 border border-racing-line text-racing-muted hover:text-racing-red" aria-label="Unduh kwitansi"><DownloadSimple size={15} /></a>
                                                <button onClick={() => printReceipt(athlete.payment)} data-testid={`receipt-print-${index}`} className="p-2 border border-racing-line text-racing-muted hover:text-racing-red" aria-label="Print kwitansi"><Printer size={15} /></button>
                                                <button onClick={() => setDeleteTarget(athlete)} data-testid={`receipt-delete-${index}`} className="p-2 border border-racing-line text-racing-muted hover:text-white hover:bg-racing-red hover:border-racing-red transition-colors" aria-label="Hapus kwitansi"><Trash size={15} /></button>
                                            </div>
                                        </div>
                                    ) : <span className="text-xs text-racing-golddeep">Belum bayar</span>}
                                </td>
                                <td className="px-4 py-3">
                                    {!athlete.payment && (
                                        <div className="flex flex-col gap-2">
                                            <button onClick={() => pay(athlete)} data-testid={`dues-pay-${index}`} className="flex items-center gap-2 bg-racing-red text-white px-4 py-2 text-xs uppercase tracking-[0.15em]"><Money size={15} /> Input Iuran</button>
                                            <button onClick={() => openBulk(athlete)} data-testid={`dues-bulk-${index}`} className="flex items-center gap-2 border border-racing-gold text-racing-golddeep hover:bg-racing-gold/10 px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors"><CalendarCheck size={15} /> Multi-Bulan</button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="flex items-center gap-2 text-xs text-racing-muted"><Receipt size={16} /> Setiap input iuran otomatis menjadi pemasukan dan membuat kwitansi PNG.</p>
            </>
            )}

            <Dialog open={!!defaultPrompt} onOpenChange={(value) => { if (!value) setDefaultPrompt(null); }}>
                <DialogContent className="max-w-md" data-testid="dues-default-prompt-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase text-xl">Jadikan Iuran Default?</DialogTitle>
                        <DialogDescription className="text-sm text-racing-muted">
                            Ingin mengubah nominal ini menjadi iuran default atlet tersebut?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="border border-racing-line bg-racing-surface p-4 text-sm space-y-1.5" data-testid="dues-default-prompt-detail">
                        <div className="flex justify-between"><span className="text-racing-muted">Atlet</span><span className="font-medium text-neutral-900">{defaultPrompt?.athlete.name}</span></div>
                        <div className="flex justify-between">
                            <span className="text-racing-muted">Default saat ini</span>
                            <span>{defaultPrompt ? (Number(defaults[defaultPrompt.athlete.id] || 0) > 0 ? `Rp ${Number(defaults[defaultPrompt.athlete.id]).toLocaleString("id-ID")}` : "Belum diatur") : ""}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-racing-muted">Default baru</span>
                            <span className="font-display text-racing-red text-lg">{defaultPrompt ? `Rp ${defaultPrompt.amount.toLocaleString("id-ID")}` : ""}</span>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-2">
                        <button
                            type="button"
                            onClick={confirmDefaultFromPrompt}
                            disabled={defaultSaving}
                            data-testid="dues-default-prompt-yes"
                            className="flex-1 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] py-3.5 transition-colors"
                        >
                            {defaultSaving ? "Menyimpan..." : "Ya, Jadikan Default"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setDefaultPrompt(null)}
                            data-testid="dues-default-prompt-no"
                            className="flex-1 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-neutral-400 text-xs uppercase tracking-[0.2em] py-3.5 transition-colors"
                        >
                            Tidak
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteTarget} onOpenChange={(value) => { if (!value) setDeleteTarget(null); }}>
                <DialogContent className="max-w-md" data-testid="receipt-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase text-xl">Hapus Kwitansi Ini?</DialogTitle>
                        <DialogDescription className="text-sm text-racing-muted">
                            Kwitansi <span className="font-medium text-neutral-900">{deleteTarget?.payment?.receipt_no}</span> untuk {deleteTarget?.name} akan dihapus. Catatan pemasukan di laporan keuangan otomatis ikut terkoreksi, dan Anda bisa membuat kwitansi baru setelahnya. Admin akan mendapat pemberitahuan.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-3 mt-2">
                        <button
                            type="button"
                            onClick={removePayment}
                            disabled={deleting}
                            data-testid="receipt-delete-confirm"
                            className="flex-1 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] py-3.5 transition-colors"
                        >
                            {deleting ? "Menghapus..." : "Ya, Hapus Kwitansi"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setDeleteTarget(null)}
                            data-testid="receipt-delete-cancel"
                            className="flex-1 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-neutral-400 text-xs uppercase tracking-[0.2em] py-3.5 transition-colors"
                        >
                            Batal
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!bulkTarget} onOpenChange={(value) => { if (!value) setBulkTarget(null); }}>
                <DialogContent className="max-w-md" data-testid="dues-bulk-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase text-xl">Input Iuran Multi-Bulan</DialogTitle>
                        <DialogDescription className="text-sm text-racing-muted">
                            {bulkTarget?.name} — pilih beberapa bulan sekaligus, setiap bulan mendapat kwitansi masing-masing.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Bulan yang belum lunas</p>
                            {bulkAvailable.length === 0 ? (
                                <p className="text-sm text-racing-muted" data-testid="dues-bulk-empty">Semua bulan yang bisa dibayar sudah lunas.</p>
                            ) : (
                                <div className="flex flex-wrap gap-2" data-testid="dues-bulk-months">
                                    {bulkAvailable.map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => toggleBulkMonth(m)}
                                            data-testid={`dues-bulk-month-${m}`}
                                            className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors ${
                                                bulkSelected.includes(m) ? "bg-racing-red border-racing-red text-white" : "bg-white border-racing-line text-racing-muted hover:border-racing-red hover:text-racing-red"
                                            }`}
                                        >
                                            {m}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nominal per Bulan (Rp)</label>
                                <input type="number" min="1" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} data-testid="dues-bulk-amount" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Diskon per Bulan (Rp)</label>
                                <input type="number" min="0" value={bulkDiscount} onChange={(e) => setBulkDiscount(e.target.value)} placeholder="0" data-testid="dues-bulk-discount" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Metode</label>
                            <input type="text" value={bulkMethod} onChange={(e) => setBulkMethod(e.target.value)} data-testid="dues-bulk-method" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div className="border border-racing-gold/50 bg-racing-gold/5 p-3 text-sm flex justify-between" data-testid="dues-bulk-total">
                            <span className="text-racing-muted">Total ({bulkSelected.length} bulan)</span>
                            <span className="font-display text-racing-red text-lg">
                                Rp {(bulkSelected.length * Math.max(Number(bulkAmount || 0) - Number(bulkDiscount || 0), 0)).toLocaleString("id-ID")}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={submitBulk}
                            disabled={bulkSaving || bulkSelected.length === 0}
                            data-testid="dues-bulk-submit"
                            className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors"
                        >
                            {bulkSaving ? "Memproses..." : `Buat ${bulkSelected.length} Kwitansi Sekaligus`}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
